import { useEffect, useReducer, useState } from "react";

import { type ApiError, toApiError } from "../../api/client";
import { createAudit, deleteImport, previewImport, repreviewImport, saveMapping, validateImports } from "../../api/freightAudit";
import { checkFile } from "../../lib/files";
import { friendlyError } from "../../lib/errors";
import { useAsync } from "../../hooks/useAsync";
import { navigate } from "../../hooks/router";
import type { ImportKind, ValidationReport } from "../../types/api";
import { MappingStep } from "./MappingStep";
import { UploadStep } from "./UploadStep";
import { ValidationStep } from "./ValidationStep";
import { initialWizardState, type WizardStep, wizardReducer } from "./wizardState";

const STEPS: { key: WizardStep; label: string }[] = [
  { key: "upload", label: "Upload" },
  { key: "map", label: "Map columns" },
  { key: "validate", label: "Check & run" },
];

function Stepper({ current }: { current: WizardStep }) {
  const index = STEPS.findIndex((step) => step.key === current);
  return (
    <ol className="stepper" aria-label="Audit steps">
      {STEPS.map((step, position) => (
        <li
          key={step.key}
          className={position < index ? "stepper__done" : position === index ? "stepper__current" : undefined}
          aria-current={position === index ? "step" : undefined}
        >
          <span className="stepper__number">{position + 1}</span>
          {step.label}
        </li>
      ))}
    </ol>
  );
}

function discard(importId: string | undefined) {
  if (importId) void deleteImport(importId).catch(() => undefined); // best effort; expiry cleans up otherwise
}

export function NewAuditWizard() {
  const [state, dispatch] = useReducer(wizardReducer, initialWizardState);
  const [uploading, setUploading] = useState(false);
  const [layoutBusy, setLayoutBusy] = useState<Record<ImportKind, boolean>>({ shipment: false, invoice: false });
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<ApiError | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [state.step]);

  const inputs =
    state.shipment.preview && state.invoice.preview
      ? {
          shipmentImportId: state.shipment.preview.import_id,
          invoiceImportId: state.invoice.preview.import_id,
          shipmentMapping: state.shipment.mapping,
          invoiceMapping: state.invoice.mapping,
          settings: { money_tolerance: state.settings.money.trim(), weight_tolerance: state.settings.weight.trim() },
        }
      : null;
  // Tolerances don't change validation, so they are not part of the key.
  const validationKey =
    state.step === "validate" && inputs
      ? JSON.stringify([inputs.shipmentImportId, inputs.invoiceImportId, inputs.shipmentMapping, inputs.invoiceMapping])
      : "idle";
  const validation = useAsync<ValidationReport | undefined>(
    (signal) =>
      inputs && state.step === "validate"
        ? validateImports(inputs, signal)
        : Promise.resolve(undefined),
    validationKey,
  );

  function onFile(kind: ImportKind, file: File | null) {
    discard(state[kind].preview?.import_id);
    dispatch({ type: "file", kind, file, error: file ? checkFile(file) : null });
  }

  async function onContinueFromUpload() {
    const { shipment, invoice } = state;
    if (!shipment.file || !invoice.file) return;
    setUploading(true);
    // A file already previewed (e.g. after "Back") is reused rather than uploaded again.
    const results = await Promise.allSettled([
      shipment.preview ? Promise.resolve(shipment.preview) : previewImport("shipment", shipment.file),
      invoice.preview ? Promise.resolve(invoice.preview) : previewImport("invoice", invoice.file),
    ]);
    setUploading(false);
    let ok = true;
    (["shipment", "invoice"] as const).forEach((kind, index) => {
      const result = results[index];
      if (result?.status === "fulfilled") {
        dispatch({ type: "previewed", kind, preview: result.value });
      } else {
        ok = false;
        const friendly = friendlyError(toApiError(result?.reason));
        dispatch({ type: "uploadFailed", kind, message: `${friendly.title} ${friendly.message}` });
      }
    });
    if (ok) dispatch({ type: "step", step: "map" });
  }

  async function onLayout(kind: ImportKind, layout: { sheet?: string; headerRow?: number }) {
    const preview = state[kind].preview;
    if (!preview) return;
    setLayoutBusy((busy) => ({ ...busy, [kind]: true }));
    try {
      const next = await repreviewImport(kind, preview.import_id, {
        sheet: layout.sheet ?? preview.sheet_name,
        headerRow: layout.headerRow ?? (layout.sheet ? null : preview.header_row),
      });
      dispatch({ type: "previewed", kind, preview: next });
    } catch (error) {
      const friendly = friendlyError(toApiError(error));
      dispatch({ type: "uploadFailed", kind, message: `${friendly.title} ${friendly.message}` });
      dispatch({ type: "step", step: "upload" });
    } finally {
      setLayoutBusy((busy) => ({ ...busy, [kind]: false }));
    }
  }

  async function onRun() {
    if (!inputs) return;
    setRunning(true);
    setRunError(null);
    try {
      for (const kind of ["shipment", "invoice"] as const) {
        const side = state[kind];
        if (side.saveMapping && side.mappingName.trim()) {
          await saveMapping(kind, side.mappingName.trim(), side.mapping);
        }
      }
      const run = await createAudit(inputs);
      navigate(`/audits/${run.id}`);
    } catch (error) {
      setRunError(toApiError(error));
      setRunning(false);
    }
  }

  return (
    <div className="wizard">
      <Stepper current={state.step} />
      {state.step === "upload" ? (
        <UploadStep
          shipmentFile={state.shipment.file}
          invoiceFile={state.invoice.file}
          shipmentError={state.shipment.fileError}
          invoiceError={state.invoice.fileError}
          busy={uploading}
          onFile={onFile}
          onContinue={onContinueFromUpload}
        />
      ) : null}
      {state.step === "map" ? (
        <MappingStep
          shipment={state.shipment}
          invoice={state.invoice}
          busy={layoutBusy}
          onMap={(kind, field, column) => dispatch({ type: "map", kind, field, column })}
          onLayout={onLayout}
          onSaveMapping={(kind, enabled, name) => dispatch({ type: "saveMapping", kind, enabled, name })}
          onBack={() => dispatch({ type: "step", step: "upload" })}
          onContinue={() => dispatch({ type: "step", step: "validate" })}
        />
      ) : null}
      {state.step === "validate" ? (
        <ValidationStep
          report={validation.data}
          loading={validation.loading}
          error={validation.error}
          settings={state.settings}
          running={running}
          runError={runError}
          onSettings={(change) => dispatch({ type: "settings", ...change })}
          onRetry={validation.reload}
          onBack={() => {
            setRunError(null);
            dispatch({ type: "step", step: "map" });
          }}
          onRun={onRun}
        />
      ) : null}
    </div>
  );
}
